import React, { useEffect, useRef, useState } from 'react';

const ROAD_MIN_X = 12;
const ROAD_MAX_X = 88;
const PLAYER_Y = 84;
const PLAYER_SIZE = 10;
const OBSTACLE_SIZE = 10;
const OBSTACLE_COUNT = 6;
const OBSTACLE_SPEED = 1.05;
const LANE_X_POSITIONS = [24, 38, 50, 62, 76];

let retroSfxContext = null;

// This function returns a shared AudioContext used for all 8-bit game sound effects.
function getRetroSfxContext() {
  if (!retroSfxContext || retroSfxContext.state === 'closed') {
    retroSfxContext = new AudioContext();
  }
  if (retroSfxContext.state === 'suspended') {
    retroSfxContext.resume();
  }
  return retroSfxContext;
}

// This function plays a single square-wave tone to create retro arcade style notes.
function playSquareTone(context, frequency, startTime, duration, volume = 0.2) {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.type = 'square';
  oscillator.frequency.value = frequency;
  gain.gain.setValueAtTime(volume, startTime);
  gain.gain.setValueAtTime(0, startTime + duration);
  oscillator.start(startTime);
  oscillator.stop(startTime + duration + 0.01);
}

// This function plays a short ascending jingle when a run starts or restarts.
function playStartSound() {
  try {
    const context = getRetroSfxContext();
    const t = context.currentTime;
    [330, 440, 554, 660].forEach((note, index) => {
      playSquareTone(context, note, t + index * 0.06, 0.055, 0.18);
    });
  } catch (error) {
    // This catch intentionally ignores audio errors when browser autoplay blocks sound.
  }
}

// This function plays a sharp impact sound immediately when a collision happens.
function playCollisionSound() {
  try {
    const context = getRetroSfxContext();
    const t = context.currentTime;
    [880, 620, 420].forEach((note, index) => {
      playSquareTone(context, note, t + index * 0.04, 0.05, 0.24);
    });
  } catch (error) {
    // This catch intentionally ignores audio errors when browser autoplay blocks sound.
  }
}

// This function plays a descending 8-bit game over jingle after a crash.
function playGameOverSound() {
  try {
    const context = getRetroSfxContext();
    const t = context.currentTime;
    [523, 415, 311, 233, 196].forEach((note, index) => {
      playSquareTone(context, note, t + index * 0.09, 0.085, 0.2);
    });
  } catch (error) {
    // This catch intentionally ignores audio errors when browser autoplay blocks sound.
  }
}

// This function keeps a numeric value inside a minimum and maximum boundary.
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

// This function creates the starting list of obstacles with staggered vertical positions.
function createInitialObstacles() {
  return Array.from({ length: OBSTACLE_COUNT }, (_, index) => ({
    id: index,
    x: LANE_X_POSITIONS[index % LANE_X_POSITIONS.length],
    y: -index * 28,
    speed: OBSTACLE_SPEED,
  }));
}

// This component renders and runs the Crazy Cars game using only React and DOM/CSS.
export default function CrazyCars() {
  const [playerX, setPlayerX] = useState(50);
  const [score, setScore] = useState(0);
  const [isGameOver, setGameOver] = useState(false);
  const [obstacles, setObstacles] = useState(() => createInitialObstacles());

  const playerXRef = useRef(50);
  const gameOverRef = useRef(false);
  const touchStartXRef = useRef(null);

  // This function moves the player car left or right and clamps movement inside the road.
  const movePlayer = (direction) => {
    if (gameOverRef.current) {
      return;
    }

    setPlayerX((prev) => {
      const next = clamp(prev + direction * 6, ROAD_MIN_X, ROAD_MAX_X);
      playerXRef.current = next;
      return next;
    });
  };

  // This function handles keyboard steering with arrow keys.
  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      movePlayer(-1);
    }
    if (event.key === 'ArrowRight') {
      movePlayer(1);
    }
  };

  // This function stores the horizontal start point for touch steering.
  const handleTouchStart = (event) => {
    touchStartXRef.current = event.touches[0].clientX;
  };

  // This function converts horizontal touch movement into left or right steering steps.
  const handleTouchMove = (event) => {
    if (gameOverRef.current || touchStartXRef.current === null) {
      return;
    }

    const nextTouchX = event.touches[0].clientX;
    const deltaX = nextTouchX - touchStartXRef.current;

    if (Math.abs(deltaX) >= 14) {
      movePlayer(deltaX > 0 ? 1 : -1);
      touchStartXRef.current = nextTouchX;
    }
  };

  // This function clears the touch drag state after a touch interaction ends.
  const handleTouchEnd = () => {
    touchStartXRef.current = null;
  };

  // This effect binds and unbinds keyboard events for desktop controls.
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  });

  // This effect tries to play the start jingle when the component is first shown.
  useEffect(() => {
    playStartSound();
  }, []);

  // This effect runs the main game loop for obstacle movement, collisions, and score updates.
  useEffect(() => {
    let animationFrameId;
    let lastTime = performance.now();

    const loop = (currentTime) => {
      const deltaSeconds = (currentTime - lastTime) / 1000;
      lastTime = currentTime;

      if (!gameOverRef.current) {
        setScore((prev) => prev + Math.floor(deltaSeconds * 100));

        setObstacles((prev) => {
          let collided = false;

          const next = prev.map((obstacle) => {
            let nextY = obstacle.y + deltaSeconds * obstacle.speed * 38;
            let nextX = obstacle.x;

            if (nextY > 120) {
              const minY = prev
                .filter((other) => other.id !== obstacle.id)
                .reduce((lowestY, other) => Math.min(lowestY, other.y), 0);
              nextY = minY - 28;
              nextX = LANE_X_POSITIONS[Math.floor(Math.random() * LANE_X_POSITIONS.length)];
            }

            const overlapY = nextY > PLAYER_Y - OBSTACLE_SIZE && nextY < PLAYER_Y + PLAYER_SIZE * 0.4;
            const overlapX = Math.abs(nextX - playerXRef.current) < (PLAYER_SIZE + OBSTACLE_SIZE) * 0.42;

            if (overlapY && overlapX) {
              collided = true;
            }

            return {
              ...obstacle,
              x: nextX,
              y: nextY,
            };
          });

          if (collided) {
            gameOverRef.current = true;
            setGameOver(true);
            playCollisionSound();
            playGameOverSound();
          }

          return next;
        });
      }

      animationFrameId = window.requestAnimationFrame(loop);
    };

    animationFrameId = window.requestAnimationFrame(loop);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, []);

  // This function resets game state so the user can immediately start a new run.
  const restartGame = () => {
    setScore(0);
    setPlayerX(50);
    setObstacles(createInitialObstacles());
    setGameOver(false);
    playerXRef.current = 50;
    gameOverRef.current = false;
    playStartSound();
  };

  const laneMarkers = Array.from({ length: 14 }, (_, index) => index);
  const skylineBars = Array.from({ length: 20 }, (_, index) => ({
    id: index,
    width: 18 + (index % 4) * 8,
    height: 28 + ((index * 13) % 40),
    left: index * 5,
  }));
  const formattedScore = String(score).padStart(6, '0');

  return (
    <div
      style={{
        width: '100vw',
        height: '100svh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background:
          'radial-gradient(circle at 50% 12%, #3e136a 0%, #1a0636 35%, #090116 62%, #03000d 100%)',
        overflow: 'hidden',
        touchAction: 'none',
        position: 'relative',
        fontFamily: "'Courier New', monospace",
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'repeating-linear-gradient(180deg, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, transparent 2px, transparent 4px)',
          opacity: 0.2,
          zIndex: 1,
        }}
      />

      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          position: 'relative',
          width: 'min(94vw, 500px)',
          height: 'min(92svh, 780px)',
          border: '2px solid #00ffd5aa',
          boxShadow: '0 0 24px #00ffd577, 0 0 52px #ff00b455, inset 0 0 48px #220038',
          borderRadius: '10px',
          overflow: 'hidden',
          background:
            'linear-gradient(180deg, #23003f 0%, #110024 28%, #070013 54%, #03000a 100%)',
          zIndex: 2,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '8%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '52%',
            aspectRatio: '1 / 1',
            borderRadius: '50%',
            background:
              'linear-gradient(180deg, rgba(255,196,0,0.92) 0%, rgba(255,102,0,0.9) 60%, rgba(255,26,121,0.75) 100%)',
            boxShadow: '0 0 60px rgba(255,120,0,0.28)',
            opacity: 0.9,
          }}
        />

        <div
          style={{
            position: 'absolute',
            top: '8%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '52%',
            height: '26%',
            background:
              'repeating-linear-gradient(180deg, transparent 0px, transparent 11px, rgba(255,0,120,0.32) 12px, rgba(255,0,120,0.32) 14px)',
            borderRadius: '50%',
            overflow: 'hidden',
          }}
        />

        {skylineBars.map((bar) => (
          <div
            key={bar.id}
            style={{
              position: 'absolute',
              bottom: '58%',
              left: `${bar.left}%`,
              width: `${bar.width}px`,
              height: `${bar.height}px`,
              background: 'linear-gradient(180deg, #2b0f5f 0%, #14042f 100%)',
              borderTop: '2px solid #ff00b4aa',
              boxShadow: '0 0 10px #ff00b433',
              opacity: 0.8,
            }}
          />
        ))}

        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '76%',
            height: '100%',
            borderLeft: '3px solid #ff00b499',
            borderRight: '3px solid #ff00b499',
            boxSizing: 'border-box',
            background:
              'linear-gradient(180deg, rgba(22,24,50,0.1) 0%, rgba(8,11,24,0.9) 35%, rgba(8,11,24,1) 100%)',
          }}
        />

        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%) perspective(420px) rotateX(42deg)',
            width: '76%',
            height: '130%',
            transformOrigin: 'top',
            background:
              'repeating-linear-gradient(90deg, rgba(0,255,204,0.15) 0px, rgba(0,255,204,0.15) 1px, transparent 1px, transparent 36px)',
            opacity: 0.35,
            pointerEvents: 'none',
          }}
        />

        {laneMarkers.map((index) => (
          <div
            key={index}
            style={{
              position: 'absolute',
              left: '50%',
              top: `${(index * 11 + (score % 11)) % 110 - 10}%`,
              transform: 'translateX(-50%)',
              width: '1.8%',
              height: '8%',
              borderRadius: '2px',
              background: 'linear-gradient(180deg, #ffee88 0%, #ffb347 100%)',
              boxShadow: '0 0 8px #ffee8890',
              opacity: 0.9,
            }}
          />
        ))}

        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'radial-gradient(circle at 50% 50%, transparent 42%, rgba(0,0,0,0.52) 100%)',
            zIndex: 4,
          }}
        />

        <div
          style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            right: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: '#00ffd5',
            fontSize: '0.95rem',
            fontWeight: 'bold',
            letterSpacing: '2px',
            textShadow: '0 0 10px #00ffd5',
            zIndex: 10,
            background: 'linear-gradient(180deg, rgba(10,0,22,0.95) 0%, rgba(10,0,22,0.35) 100%)',
            border: '1px solid #00ffd566',
            borderRadius: '6px',
            padding: '8px 10px',
          }}
        >
          <span style={{ color: '#ff4bc8', textShadow: '0 0 10px #ff4bc8' }}>CRAZY CARS 84</span>
          <span>SCORE {formattedScore}</span>
        </div>

        {obstacles.map((obstacle) => (
          <div
            key={obstacle.id}
            style={{
              position: 'absolute',
              left: `${obstacle.x}%`,
              top: `${obstacle.y}%`,
              width: `${OBSTACLE_SIZE}%`,
              height: `${OBSTACLE_SIZE}%`,
              transform: 'translate(-50%, -50%)',
              borderRadius: '3px',
              border: '2px solid #ffd2a6',
              background: 'linear-gradient(180deg, #ff4f7a 0%, #cc003f 100%)',
              boxShadow: '0 0 12px #ff005d99',
              zIndex: 6,
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '52%',
                left: '12%',
                width: '76%',
                height: '24%',
                background: 'rgba(80, 0, 30, 0.35)',
                borderTop: '1px solid #ff8cab88',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '18%',
                left: '18%',
                width: '64%',
                height: '28%',
                background: 'rgba(146, 236, 255, 0.9)',
                border: '1px solid #ffffff66',
                borderRadius: '2px',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '8%',
                left: '26%',
                width: '48%',
                height: '10%',
                background: '#ffc1d6',
                opacity: 0.85,
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '48%',
                left: '30%',
                width: '40%',
                height: '5%',
                background: '#ffc9da',
                opacity: 0.8,
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '12%',
                left: '-8%',
                width: '16%',
                height: '20%',
                borderRadius: '50%',
                background: '#141414',
                border: '1px solid #777',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '12%',
                right: '-8%',
                width: '16%',
                height: '20%',
                borderRadius: '50%',
                background: '#141414',
                border: '1px solid #777',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: '10%',
                left: '-8%',
                width: '16%',
                height: '20%',
                borderRadius: '50%',
                background: '#141414',
                border: '1px solid #777',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: '10%',
                right: '-8%',
                width: '16%',
                height: '20%',
                borderRadius: '50%',
                background: '#141414',
                border: '1px solid #777',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '16%',
                left: '-3%',
                width: '6%',
                height: '12%',
                borderRadius: '50%',
                background: '#9a9a9a',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '16%',
                right: '-3%',
                width: '6%',
                height: '12%',
                borderRadius: '50%',
                background: '#9a9a9a',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: '14%',
                left: '-3%',
                width: '6%',
                height: '12%',
                borderRadius: '50%',
                background: '#9a9a9a',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: '14%',
                right: '-3%',
                width: '6%',
                height: '12%',
                borderRadius: '50%',
                background: '#9a9a9a',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '42%',
                left: '2%',
                width: '10%',
                height: '14%',
                borderRadius: '2px',
                background: '#ffe7a3',
                boxShadow: '0 0 6px #ffe7a3',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '42%',
                right: '2%',
                width: '10%',
                height: '14%',
                borderRadius: '2px',
                background: '#ff1c3f',
                boxShadow: '0 0 6px #ff1c3f',
              }}
            />
          </div>
        ))}

        <div
          style={{
            position: 'absolute',
            left: `${playerX}%`,
            top: `${PLAYER_Y}%`,
            width: `${PLAYER_SIZE}%`,
            height: `${PLAYER_SIZE}%`,
            transform: 'translate(-50%, -50%)',
            borderRadius: '3px',
            background: isGameOver
              ? 'linear-gradient(180deg, #676767 0%, #2a2a2a 100%)'
              : 'linear-gradient(180deg, #00ffd5 0%, #00a0ff 100%)',
            border: '2px solid #fff2aa',
              boxShadow: isGameOver ? 'none' : '0 0 16px #00ffd5aa, 0 0 24px #00a0ff88',
              zIndex: 8,
            }}
        >
          <div
            style={{
              position: 'absolute',
              top: '52%',
              left: '12%',
              width: '76%',
              height: '24%',
              background: 'rgba(0, 36, 60, 0.35)',
              borderTop: '1px solid #98fff188',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '18%',
              left: '18%',
              width: '64%',
              height: '28%',
              background: 'rgba(215, 250, 255, 0.95)',
              border: '1px solid #ffffff99',
              borderRadius: '2px',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '8%',
              left: '26%',
              width: '48%',
              height: '10%',
              background: '#d8fffb',
              opacity: 0.9,
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '48%',
              left: '30%',
              width: '40%',
              height: '5%',
              background: '#e6fffd',
              opacity: 0.9,
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '14%',
              left: '18%',
              width: '64%',
              height: '12%',
              background: '#ff3fb7',
              boxShadow: '0 0 10px #ff3fb7aa',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '12%',
              left: '-8%',
              width: '16%',
              height: '20%',
              borderRadius: '50%',
              background: '#141414',
              border: '1px solid #777',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '12%',
              right: '-8%',
              width: '16%',
              height: '20%',
              borderRadius: '50%',
              background: '#141414',
              border: '1px solid #777',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '10%',
              left: '-8%',
              width: '16%',
              height: '20%',
              borderRadius: '50%',
              background: '#141414',
              border: '1px solid #777',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '10%',
              right: '-8%',
              width: '16%',
              height: '20%',
              borderRadius: '50%',
              background: '#141414',
              border: '1px solid #777',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '16%',
              left: '-3%',
              width: '6%',
              height: '12%',
              borderRadius: '50%',
              background: '#9a9a9a',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '16%',
              right: '-3%',
              width: '6%',
              height: '12%',
              borderRadius: '50%',
              background: '#9a9a9a',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '14%',
              left: '-3%',
              width: '6%',
              height: '12%',
              borderRadius: '50%',
              background: '#9a9a9a',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '14%',
              right: '-3%',
              width: '6%',
              height: '12%',
              borderRadius: '50%',
              background: '#9a9a9a',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '42%',
              left: '2%',
              width: '10%',
              height: '14%',
              borderRadius: '2px',
              background: '#ffe7a3',
              boxShadow: '0 0 6px #ffe7a3',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '42%',
              right: '2%',
              width: '10%',
              height: '14%',
              borderRadius: '2px',
              background: '#ff1c3f',
              boxShadow: '0 0 6px #ff1c3f',
            }}
          />
        </div>

        {isGameOver && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(5, 0, 20, 0.86)',
              backdropFilter: 'blur(2px)',
              zIndex: 20,
              color: '#ff4bc8',
            }}
          >
            <h1
              style={{
                fontSize: 'clamp(2.4rem, 8vw, 4rem)',
                margin: 0,
                letterSpacing: '4px',
                textShadow: '0 0 16px #ff4bc8, 0 0 36px #ff004f',
                animation: 'ccFlicker 0.55s ease-in-out infinite alternate',
              }}
            >
              CRASHED!
            </h1>
            <div
              style={{
                marginTop: '8px',
                color: '#00ffd5',
                letterSpacing: '3px',
                textShadow: '0 0 8px #00ffd5',
                fontSize: '0.9rem',
              }}
            >
              FINAL SCORE {formattedScore}
            </div>
            <button
              onClick={restartGame}
              style={{
                marginTop: '20px',
                padding: '12px 24px',
                fontSize: '1rem',
                letterSpacing: '2px',
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #00ffd5 0%, #00a0ff 100%)',
                color: '#120022',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                boxShadow: '0 0 24px #00ffd588',
              }}
            >
              INSERT COIN (RESTART)
            </button>
          </div>
        )}

        <style>{`
          @keyframes ccFlicker {
            0% { opacity: 1; }
            93% { opacity: 1; }
            95% { opacity: 0.35; }
            100% { opacity: 0.95; }
          }
        `}</style>
      </div>
    </div>
  );
}
